import React from "react";
import { Box, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CancelIcon from "@mui/icons-material/Cancel";
import { performanceStatusColor } from "../../../utils/constant";

const IconBadge = ({ icon: Icon, color }) => (
  <Box
    sx={{
      width: "22px",
      height: "22px",
      borderRadius: "50%",
      backgroundColor: color,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}
  >
    <Icon sx={{ fontSize: "13px", color: "#fff" }} />
  </Box>
);

const Metric = ({ icon, color, label, value }) => (
  <Box display="flex" alignItems="center" gap="0.35rem" whiteSpace="nowrap">
    <IconBadge icon={icon} color={color} />
    <Typography fontSize={"0.82rem"} color={"#444"}>
      {label}
    </Typography>
    <Typography fontSize={"0.9rem"} fontWeight={700} color={"#111"} ml="0.4rem">
      {value ?? 0}
    </Typography>
  </Box>
);

// One flat 5-col x 2-row grid (not two separate row-grids) — that's what
// makes a column's divider a single border spanning both rows with zero gap.
// Equal-width columns match the reference layout (5 even segments); content
// sizing (minmax(max-content, 1fr)) made short-text columns like "Delayed"
// balloon out to absorb the leftover space instead of staying even.
const GRID_COLUMNS = "repeat(5, 1fr)";

const Cell = ({ lastCol, lastRow, children }) => (
  <Box
    sx={{
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "0.7rem 1.25rem",
      borderBottom: lastRow ? "none" : "1px solid #EFEFEF",
      minWidth: 0,
      overflow: "hidden",
      // Inset top/bottom so the divider reads as a short, discontinued
      // segment rather than one solid line running the full cell height.
      "&::after": lastCol
        ? undefined
        : {
            content: '""',
            position: "absolute",
            top: "22%",
            bottom: "22%",
            right: 0,
            width: "1px",
            backgroundColor: "#E5E5E5",
          },
    }}
  >
    {children}
  </Box>
);

const TotalCell = ({ label, value, lastRow }) => (
  <Cell lastRow={lastRow}>
    <Typography fontSize={"0.82rem"} color={"#444"} component="span" whiteSpace="nowrap">
      {label}
    </Typography>
    <Typography fontSize={"0.9rem"} fontWeight={700} color={"#111"} component="span" ml="0.4rem">
      {value ?? 0}
    </Typography>
  </Cell>
);

// Top KPI strip above the Task/Approval Performance sections — reads straight
// off the `summary` object each analytics endpoint returns.
const PerformanceSummaryBar = ({ taskSummary, approvalSummary }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: GRID_COLUMNS,
      border: "1px solid rgba(232, 232, 232, 1)",
      borderRadius: "6px",
      overflowX: "auto",
      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.06)",
    }}
  >
    {/* Row 1 — Task */}
    <TotalCell label="Total Task" value={taskSummary?.total} />
    <Cell>
      <Metric icon={EditIcon} color={performanceStatusColor.pending} label="Pending" value={taskSummary?.pending} />
    </Cell>
    <Cell>
      <Metric icon={CheckIcon} color={performanceStatusColor.done} label="Completed" value={taskSummary?.completed} />
    </Cell>
    <Cell>
      <Metric icon={AccessTimeIcon} color={performanceStatusColor.started_pending} label="In Progress" value={taskSummary?.inProgress} />
    </Cell>
    <Cell lastCol>
      <Metric icon={ErrorOutlineIcon} color={performanceStatusColor.delayed_pending} label="Delayed" value={taskSummary?.delayed} />
    </Cell>

    {/* Row 2 — Approval */}
    <TotalCell label="Total Approval" value={approvalSummary?.total} lastRow />
    <Cell lastRow>
      <Metric icon={EditIcon} color={performanceStatusColor.pending} label="Pending" value={approvalSummary?.pending} />
    </Cell>
    <Cell lastRow>
      <Metric icon={CheckIcon} color={performanceStatusColor.done} label="Approved" value={approvalSummary?.approved} />
    </Cell>
    <Cell lastRow>
      <Metric icon={CancelIcon} color={performanceStatusColor.rejected} label="Rejected" value={approvalSummary?.rejected} />
    </Cell>
    <Cell lastCol lastRow>
      <Metric icon={ErrorOutlineIcon} color={performanceStatusColor.delayed_pending} label="Delayed" value={approvalSummary?.delayed} />
    </Cell>
  </Box>
);

export default PerformanceSummaryBar;
